import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import MonacoHtmlEditor from "../components/MonacoEditor";
import { useForm } from "react-hook-form";
import { formFields } from "../helper/templateconstant";
import { Card, CardBody, Container, Col, Row, Button } from "reactstrap";

import CommonTabHeader from "Components/Common/CommonTabHeader";
import { BreadcrumbContext } from "Layouts/Header";
import { showToastMessage } from "common/service/CommonService";
import { LIBRARY_PATHS } from "Routes/paths";
import { useTemplateApi } from "modules/Library/Template/api/useTemplateService";
import { queryClient } from "api/queryClient";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";

const AddTemplate: React.FC = () => {
  document.title = "Add Template | ZENTIXS VISTA";
  const navigate = useNavigate();

  const [htmlContent, setHtmlContent] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  // Form Setup
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      templateName: "",
    },
    mode: "onChange",
  });

  // API Hook
  const { InsertMutation } = useTemplateApi({ selectAllEnabled: false });

  // Breadcrumbs: set for this page
  const breadcrumbContext = useContext(BreadcrumbContext);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Template", link: LIBRARY_PATHS.TEMPLATE_LIST },
      { text: "Add Template" },
    ]);

    return () => {
      breadcrumbContext?.setBreadcrumbItems?.([]);
    };
  }, []);

  // ✅ Proper HTML validation
  const isHtmlEmpty = (html: string) => {
    return !html || html.trim() === "";
  };

  const handleSave = (data: any) => {
    if (isHtmlEmpty(htmlContent)) {
      showToastMessage("error", "Please enter the HTML template content");
      return;
    }

    InsertMutation.mutate(
      {
        name: data.templateName,
        html: htmlContent,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res?.message || "Template saved successfully",
          );
          queryClient.invalidateQueries({ queryKey: ["template-selectAll"] });
          navigate(LIBRARY_PATHS.TEMPLATE_LIST);
        },
        onError: (error: any) => {
          showToastMessage("error", (error?.message ?? error) || "Failed to save template");
        },
      },
    );
  };

  return (
    <Container
      fluid
      style={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "stretch",
      }}
    >
      <CommonTabHeader
        title="Add Template"
        showAction={true}
        showActionInHeader={false}
        activeTab="Save Template"
        showActionHandler={handleSubmit(handleSave)}
        exitPath={LIBRARY_PATHS.TEMPLATE_LIST}
        isOpen={InsertMutation.isPending}
      />

      <Row className="pt-2 flex-grow-1 overflow-hidden">
        <Col lg={12} className="h-100 pb-2">
          <Card className="h-100 shadow-none border-0 mb-0">
            <CardBody className="p-3 d-flex flex-column h-100 overflow-hidden">
              <div className="text-end template_btn_container">
                <Button
                  size="sm"
                  color="primary"
                  outline
                  onClick={handleSubmit(handleSave)}
                >
                  {InsertMutation.isPending ? (
                    <span className="spinner-border spinner-border-sm me-1" />
                  ) : null}
                  Save Template
                </Button>
              </div>
              {/* FORM FIELDS */}
              <CommonForm
                fields={formFields}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />

              {/* INTEGRATED EDITOR / PREVIEW */}
              <div
                className="flex-grow-1 mt-3 overflow-hidden w-100"
                style={{ minHeight: "0" }}
              >
                <MonacoHtmlEditor
                  value={htmlContent}
                  onChange={setHtmlContent}
                  height="100%"
                  showPreview={showPreview}
                  onTogglePreview={() => setShowPreview(!showPreview)}
                />
              </div>

              {/* NOTE - Stay at bottom */}


              <div className="mt-2 small text-muted pt-2 border-top">
                <strong>Note:</strong> Use placeholders like{" "}
                <code>{"{{FullName}}"}</code>, <code>{"{{Barcode}}"}</code>,{" "}
                <code>{"{{SubjectNo}}"}</code>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AddTemplate;
